import { useState, useEffect, useRef } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { Send, Plus, Trash2, ChevronDown, ChevronRight, Database, MessageSquare } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const SUGGESTED = [
  "What is our total portfolio AUM and top 5 holdings by weight?",
  "Compare our tech sector exposure against the rest of the portfolio",
  "What were the S&P 500 returns during 2020 to 2024?",
  "Which stocks have the highest analyst consensus rating?",
  "Show me the unrealized P&L breakdown by sector",
  "What is the average analyst score for technology stocks?",
];

function SqlStep({ step }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2 border border-[#A8A67A]/20 text-xs">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-[#F5F4EE] hover:bg-[#e8ede4] text-[#A8A67A] font-medium transition-colors"
      >
        <Database className="w-3.5 h-3.5" />
        <span>SQL Query {step.step}: {step.reason}</span>
        <span className="ml-auto text-[10px] text-muted-foreground">{step.row_count} rows</span>
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
      </button>
      {open && (
        <div>
          <div className="sql-block text-xs">{step.sql}</div>
          {step.results && step.results.length > 0 && (
            <div className="overflow-x-auto border-t border-border">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="bg-[#F5F4EE]">
                    {Object.keys(step.results[0]).map(k => (
                      <th key={k} className="text-left px-2 py-1.5 font-semibold text-[#A8A67A] uppercase tracking-wider text-[10px]">
                        {k}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {step.results.slice(0, 8).map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-[#F8F9FA]"}>
                      {Object.values(row).map((v, j) => (
                        <td key={j} className="px-2 py-1.5 font-mono text-foreground border-t border-border">
                          {typeof v === "number" ? v.toLocaleString() : String(v ?? "")}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Message({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div className={`chat-message flex ${isUser ? "justify-end" : "justify-start"} mb-4`}>
      {!isUser && (
        <div className="w-7 h-7 bg-[#A8A67A] flex items-center justify-center flex-shrink-0 mr-2 mt-0.5">
          <MessageSquare className="w-3.5 h-3.5 text-white" />
        </div>
      )}
      <div className={`max-w-[80%] ${isUser ? "items-end" : "items-start"} flex flex-col`}>
        <div className={`px-4 py-3 text-sm leading-relaxed ${
          isUser
            ? "bg-[#A8A67A] text-white"
            : "bg-white border border-border text-foreground"
        }`}>
          {isUser ? (
            <span className="whitespace-pre-wrap">{msg.content}</span>
          ) : (
            <div className="prose-sm prose-olive">
              <ReactMarkdown
                components={{
                  p: ({ children }) => <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>,
                  strong: ({ children }) => <strong className="font-semibold text-[#A8A67A]">{children}</strong>,
                  em: ({ children }) => <em className="italic">{children}</em>,
                  ul: ({ children }) => <ul className="list-disc pl-4 mb-2 space-y-0.5">{children}</ul>,
                  ol: ({ children }) => <ol className="list-decimal pl-4 mb-2 space-y-0.5">{children}</ol>,
                  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
                  h1: ({ children }) => <h1 className="text-base font-bold text-[#A8A67A] mt-3 mb-1">{children}</h1>,
                  h2: ({ children }) => <h2 className="text-sm font-bold text-[#A8A67A] mt-3 mb-1">{children}</h2>,
                  h3: ({ children }) => <h3 className="text-sm font-semibold text-[#A8A67A] mt-2 mb-1">{children}</h3>,
                  code: ({ inline, children }) => inline
                    ? <code className="bg-[#F5F4EE] text-[#A8A67A] px-1 py-0.5 rounded text-xs font-mono">{children}</code>
                    : <pre className="bg-[#0A0A0A] text-[#C4C29A] p-3 text-xs font-mono overflow-x-auto mt-1 mb-2 rounded">{children}</pre>,
                  blockquote: ({ children }) => <blockquote className="border-l-2 border-[#A8A67A] pl-3 text-muted-foreground italic">{children}</blockquote>,
                  hr: () => <hr className="border-border my-2" />,
                }}
              >
                {msg.content}
              </ReactMarkdown>
            </div>
          )}
        </div>
        {msg.sql_steps && msg.sql_steps.length > 0 && (
          <div className="w-full mt-1">
            {msg.sql_steps.map((step, i) => (
              <SqlStep key={i} step={step} />
            ))}
          </div>
        )}
        <span className="text-[10px] text-muted-foreground mt-1 px-1">
          {new Date(msg.timestamp).toLocaleTimeString()}
        </span>
      </div>
    </div>
  );
}

export default function AIChat() {
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    loadSessions();
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  const loadSessions = async () => {
    const res = await axios.get(`${API}/chat/sessions`);
    setSessions(res.data.sessions);
  };

  const newSession = async () => {
    const res = await axios.post(`${API}/chat/session`, { title: "New Analysis" });
    const session = res.data;
    setSessions(prev => [session, ...prev]);
    setActiveSession(session);
    setMessages([]);
  };

  const selectSession = async (session) => {
    setActiveSession(session);
    const res = await axios.get(`${API}/chat/history/${session.session_id}`);
    setMessages(res.data.messages);
  };

  const deleteSession = async (e, sessionId) => {
    e.stopPropagation();
    await axios.delete(`${API}/chat/session/${sessionId}`);
    setSessions(prev => prev.filter(s => s.session_id !== sessionId));
    if (activeSession?.session_id === sessionId) {
      setActiveSession(null);
      setMessages([]);
    }
  };

  const sendMessage = async (text = input) => {
    if (!text.trim() || sending) return;
    let session = activeSession;
    if (!session) {
      const res = await axios.post(`${API}/chat/session`, { title: text.slice(0, 50) });
      session = res.data;
      setSessions(prev => [session, ...prev]);
      setActiveSession(session);
    }

    const userMsg = {
      role: "user", content: text.trim(),
      timestamp: new Date().toISOString(), message_id: "tmp-" + Date.now()
    };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setSending(true);

    try {
      const res = await axios.post(`${API}/chat/message`, {
        session_id: session.session_id, message: text.trim()
      });
      setMessages(prev => [...prev.filter(m => m.message_id !== userMsg.message_id), userMsg, res.data]);
      loadSessions();
    } catch (err) {
      setMessages(prev => [...prev, {
        role: "assistant", content: "I encountered an error. Please try again.",
        timestamp: new Date().toISOString(), message_id: "err-" + Date.now(), sql_steps: []
      }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex h-full" data-testid="ai-chat-page">
      {/* Session sidebar */}
      <div className="w-56 bg-[#F8F9FA] border-r border-border flex flex-col flex-shrink-0">
        <div className="p-3 border-b border-border">
          <button
            onClick={newSession}
            className="w-full flex items-center gap-2 bg-[#A8A67A] text-white px-3 py-2 text-xs font-semibold hover:bg-[#8E8C64] transition-colors"
            data-testid="new-session-btn"
          >
            <Plus className="w-3.5 h-3.5" />
            New Analysis
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {sessions.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-4 px-2">No sessions yet. Start a new analysis.</p>
          )}
          {sessions.map(s => (
            <div
              key={s.session_id}
              onClick={() => selectSession(s)}
              className={`group flex items-center justify-between px-3 py-2 text-xs cursor-pointer rounded-none border-l-2 transition-all ${
                activeSession?.session_id === s.session_id
                  ? "border-[#A8A67A] bg-[#F5F4EE] text-foreground font-medium"
                  : "border-transparent hover:bg-white text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`session-${s.session_id}`}
            >
              <span className="truncate flex-1">{s.title}</span>
              <button
                onClick={(e) => deleteSession(e, s.session_id)}
                className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-500 transition-all ml-1 flex-shrink-0"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Chat area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border bg-white flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="font-semibold text-foreground">Ellerston Agent</h2>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-xs text-emerald-600 font-medium">Online</span>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6" data-testid="chat-messages">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full space-y-6 text-center">
              <div className="w-12 h-12 bg-[#F5F4EE] flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-[#A8A67A]" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-1">Ellerston Agent</h3>
                <p className="text-sm text-muted-foreground max-w-sm">
                  Ask any question about the portfolio or market data. The AI will query the warehouse and provide data-backed insights.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-2xl w-full">
                {SUGGESTED.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(q)}
                    className="text-left text-xs px-4 py-3 border border-border bg-white hover:bg-[#F5F4EE] hover:border-[#D4D2B0] transition-colors text-foreground"
                    data-testid={`suggested-query-${i}`}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map(msg => <Message key={msg.message_id} msg={msg} />)}
          {sending && (
            <div className="flex items-start gap-2 mb-4 chat-message">
              <div className="w-7 h-7 bg-[#A8A67A] flex items-center justify-center flex-shrink-0">
                <MessageSquare className="w-3.5 h-3.5 text-white" />
              </div>
              <div className="bg-white border border-border px-4 py-3">
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                  <Database className="w-3 h-3 animate-pulse text-[#A8A67A]" />
                  <span>Querying warehouse...</span>
                </div>
                <div className="dot-pulse flex gap-1">
                  <span /><span /><span />
                </div>
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t border-border bg-white flex-shrink-0">
          <div className="flex gap-2">
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendMessage()}
              placeholder="Ask about portfolio holdings, market returns, analyst ratings..."
              className="flex-1 px-4 py-2.5 text-sm border border-border bg-background focus:outline-none focus:ring-1 focus:ring-[#A8A67A] rounded-none placeholder:text-muted-foreground"
              disabled={sending}
              data-testid="chat-input"
            />
            <button
              onClick={() => sendMessage()}
              disabled={!input.trim() || sending}
              className="bg-[#A8A67A] text-white px-4 py-2.5 hover:bg-[#8E8C64] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="send-btn"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-1.5 px-1">
            AI has direct access to all 3 warehouse tables · SQL queries are shown inline
          </p>
        </div>
      </div>
    </div>
  );
}
